<template>
  <div class="universal-search-wrapper" :dir="isRTL ? 'rtl' : 'ltr'">
    <!-- Search Input Trigger -->
    <div class="search-trigger" @click="openModal">
      <AppIcon name="bi-search" class="bi bi-search" />
      <input 
        type="text" 
        class="search-input-trigger" 
        :placeholder="$t('search')" 
        readonly
      />
      <kbd class="search-kbd">⌘K</kbd>
    </div>

    <!-- Search Modal - Positioned fixed, outside flow -->
    <transition name="modal-fade">
      <div
        v-if="isModalOpen"
        ref="modalBackdrop"
        class="custom-modal-backdrop"
        :dir="isRTL ? 'rtl' : 'ltr'"
        @click="closeModal"
      >
        <div class="custom-modal-dialog" @click.stop>
          <div class="search-modal-content">
              
              <!-- Modal Header -->
              <div class="search-modal-header">
                <div class="search-input-wrapper">
                  <AppIcon name="bi-search" class="bi bi-search search-icon" />
                  <input 
                    ref="searchInput"
                    v-model="searchQuery" 
                    type="text" 
                    class="search-input-main" 
                    :placeholder="selectedTopic ? $t('search_in') + ' ' + selectedTopic.label + '...' : $t('type_to_search')"
                    @input="handleSearch"
                    @keydown.down.prevent="navigateDown"
                    @keydown.up.prevent="navigateUp"
                    @keydown.enter.prevent="selectResult"
                    @keydown.esc="closeModal"
                  />
                  <button class="btn-close-search" @click="closeModal">
                    <AppIcon name="bi-x-lg" class="bi bi-x-lg" />
                  </button>
                </div>
                
                <!-- Topic Selection - Show when no query -->
                <div class="search-topics" v-if="!searchQuery && !selectedTopic">
                  <div class="topics-header">{{ $t('select_search_topic') }}</div>
                  <div class="topics-grid">
                    <div 
                      v-for="type in enabledSearchTypes" 
                      :key="type.prefix"
                      class="topic-item"
                      @click="selectTopic(type)"
                    >
                      <div class="topic-icon" :class="`icon-${type.type}`">
                        <AppIcon :name="type.icon" :class="type.icon" />
                      </div>
                      <div class="topic-label">{{ type.label }}</div>
                    </div>
                  </div>
                </div>

                <!-- Selected Topic with Search -->
                <div class="selected-topic-bar" v-if="selectedTopic && !searchQuery">
                  <div class="selected-topic-info">
                    <AppIcon :name="selectedTopic.icon" :class="selectedTopic.icon" class="me-2" />
                    <span>{{ $t('searching_in') }}: <strong>{{ selectedTopic.label }}</strong></span>
                  </div>
                  <button class="btn-clear-topic" @click="clearTopic">
                    <AppIcon name="bi-x" class="bi bi-x" />
                  </button>
                </div>
              </div>

              <!-- Modal Body -->
              <div class="search-modal-body">
            <!-- Loading State -->
            <div v-if="isLoading" class="search-loading">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">{{ $t('searching') }}...</p>
            </div>

            <!-- Search Results -->
            <div v-else-if="searchQuery && results.length > 0" class="search-results">
              <div 
                v-for="(result, index) in results" 
                :key="result.id"
                class="search-result-item"
                :class="{ 'active': selectedIndex === index }"
                @click="navigateToResult(result)"
                @mouseenter="selectedIndex = index"
              >
                <div class="result-icon" :class="`icon-${result.type}`">
                  <AppIcon :name="getIcon(result.type)" :class="getIcon(result.type)" />
                </div>
                <div class="result-content">
                  <div class="result-title">{{ result.title }}</div>
                  <div class="result-subtitle">{{ result.subtitle }}</div>
                </div>
                <div class="result-type">
                  <span class="type-badge" :class="`badge-${result.type}`">
                    {{ getTypeLabel(result.type) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- No Results -->
            <div v-else-if="searchQuery && !isLoading && results.length === 0" class="search-empty">
              <AppIcon name="bi-inbox" class="bi bi-inbox" />
              <p>{{ $t('no_results_found') }}</p>
              <small>{{ $t('try_different_keywords') }}</small>
            </div>

                <!-- Initial State - Show nothing or instructions -->
                <div v-else-if="!selectedTopic" class="search-initial">
                  <div class="initial-icon">
                    <AppIcon name="bi-search" class="bi bi-search" />
                  </div>
                  <p class="initial-text">{{ $t('select_topic_to_search') }}</p>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </transition>
  </div>
</template>

<script>
import helper from '../helper'

export default {
  name: 'UniversalSearch',
  data() {
    return {
      searchQuery: '',
      results: [],
      isLoading: false,
      selectedIndex: 0,
      isModalOpen: false,
      selectedTopic: null,
      debounceTimer: null
    }
  },
  computed: {
    searchTypes() {
      return [
        { prefix: 'CL-', label: this.$t('clients'), example: 'CL-John Doe', type: 'client', icon: 'bi-person-fill', module: 'clients' },
        { prefix: 'SUP-', label: this.$t('suppliers'), example: 'SUP-ABC Supply', type: 'supplier', icon: 'bi-truck', module: 'suppliers' },
        { prefix: 'PRD-', label: this.$t('products'), example: 'PRD-Laptop', type: 'product', icon: 'bi-box-seam', module: 'products' },
        { prefix: 'INV-', label: this.$t('inventories'), example: 'INV-2024', type: 'inventory', icon: 'bi-clipboard-data', module: 'inventories' },
        { prefix: 'CT-', label: this.$t('categories'), example: 'CT-Electronics', type: 'category', icon: 'bi-tag', module: 'categories' },
        { prefix: 'WH-', label: this.$t('warehouses'), example: 'WH-Main', type: 'warehouse', icon: 'bi-building', module: 'warehouses' },
        { prefix: 'Q-', label: this.$t('quotes'), example: 'Q-2024001', type: 'quote', icon: 'bi-file-text', module: 'quotes' },
        { prefix: 'ORD-', label: this.$t('orders'), example: 'ORD-2024001', type: 'order', icon: 'bi-cart', module: 'orders' },
        { prefix: 'DEL-', label: this.$t('deliveries'), example: 'DEL-2024001', type: 'delivery', icon: 'bi-truck', module: 'deliveries' },
        { prefix: 'IN-', label: this.$t('invoices'), example: 'IN-2024001', type: 'invoice', icon: 'bi-receipt', module: 'invoices' },
        { prefix: 'RET-', label: this.$t('returns'), example: 'RET-2024001', type: 'return', icon: 'bi-arrow-return-left', module: 'returns' },
        { prefix: 'PUR-', label: this.$t('purchases'), example: 'PUR-2024001', type: 'purchase', icon: 'bi-bag', module: 'purchases' },
        { prefix: 'RECP-', label: this.$t('receptions'), example: 'RECP-2024001', type: 'reception', icon: 'bi-inbox', module: 'receptions' },
        { prefix: 'PO-', label: this.$t('pos'), example: 'PO-2024001', type: 'pos', icon: 'bi-shop', module: 'pos' },
        { prefix: 'CARE-', label: this.$t('cash_registers'), example: 'CARE-Main', type: 'cash_register', icon: 'bi-cash-stack', module: 'pos' },
        { prefix: 'TRS-', label: this.$t('payments'), example: 'TRS-2024001', type: 'payment', icon: 'bi-credit-card', module: 'transactions' },
        { prefix: 'ATT-', label: this.$t('attachments'), example: 'ATT-Document', type: 'attachment', icon: 'bi-paperclip', module: 'attachments' },
        { prefix: 'PRJ-', label: this.$t('projects'), example: 'PRJ-Website', type: 'project', icon: 'bi-kanban', module: 'projects' },
      ]
    },
    enabledSearchTypes() {
      // Filter search types based on enabled modules
      const modules = this.$store.getters.MODULES || [];
      
      const enabledTypes = this.searchTypes.filter(type => {
        if (!type.module) {
          return true; // Always show if no module specified
        }
        
        // Check if the module item is enabled
        // The store structure is: modules[].items[] where items have title property
        let isEnabled = false;
        
        for (const module of modules) {
          if (!module.items) continue;
          
          const item = module.items.find(i => i.title === type.module);
          if (item) {
            // Check if both the parent module and the item are enabled
            isEnabled = module.enabled && item.enabled;
            break;
          }
        }
        
        // If no match found in items, check if type.module matches a module_key directly
        if (!isEnabled) {
          const directModule = modules.find(m => m.module_key === type.module);
          if (directModule) {
            isEnabled = directModule.enabled;
          }
        }
        
        return isEnabled;
      });
      
      return enabledTypes;
    },
    isRTL() {
      const currentLang = localStorage.getItem('currentLang') || 'en';
      return currentLang === 'ar';
    }
  },
  mounted() {
    // Add keyboard shortcut listener
    document.addEventListener('keydown', this.handleKeyboardShortcut)
    
    // Auto-select topic based on current route
    this.autoSelectTopicFromRoute()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyboardShortcut)
    document.body.style.overflow = ''
  },
  watch: {
    isModalOpen(newVal) {
      if (newVal) {
        // Mount the overlay at the document root so transformed navbar/sidebar
        // containers cannot limit its fixed positioning or stacking context.
        this.$nextTick(() => {
          const backdrop = this.$refs.modalBackdrop
          if (backdrop && backdrop.parentNode !== document.body) {
            document.body.appendChild(backdrop)
          }

          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
        // Prevent body scroll
        document.body.style.overflow = 'hidden'
        
        // Re-check route when modal opens
        this.autoSelectTopicFromRoute()
      } else {
        // Restore body scroll
        document.body.style.overflow = ''
      }
    },
    '$route'() {
      // Update topic when route changes
      if (!this.isModalOpen) {
        this.autoSelectTopicFromRoute()
      }
    }
  },
  methods: {
    handleKeyboardShortcut(e) {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        this.openModal()
      }
    },
    selectTopic(topic) {
      this.selectedTopic = topic
      this.$nextTick(() => {
        this.$refs.searchInput.focus()
      })
    },
    clearTopic() {
      this.selectedTopic = null
      this.searchQuery = ''
      this.results = []
    },
    autoSelectTopicFromRoute() {
      // Map routes to search types
      const currentPath = this.$route.path
      const routeMapping = {
        '/clients': 'client',
        '/suppliers': 'supplier',
        '/products': 'product',
        '/inventories': 'inventory',
        '/categories': 'category',
        '/warehouses': 'warehouse',
        '/quotes': 'quote',
        '/orders': 'order',
        '/deliveries': 'delivery',
        '/invoices': 'invoice',
        '/returns': 'return',
        '/purchases': 'purchase',
        '/receptions': 'reception',
        '/pos-transactions': 'pos',
        '/cash-registers': 'cash_register',
        '/transactions': 'payment',
      }
      
      // Check if current path matches any route
      for (const [route, type] of Object.entries(routeMapping)) {
        if (currentPath.startsWith(route)) {
          const topic = this.enabledSearchTypes.find(t => t.type === type)
          if (topic) {
            this.selectedTopic = topic
          }
          return
        }
      }
      
      // If no match, clear selected topic
      this.selectedTopic = null
    },
    openModal() {
      this.isModalOpen = true
    },
    closeModal() {
      this.isModalOpen = false
      this.searchQuery = ''
      this.results = []
      this.selectedIndex = 0
      this.selectedTopic = null
    },
    handleSearch() {
      clearTimeout(this.debounceTimer)
      
      if (this.searchQuery.trim() === '') {
        this.results = []
        return
      }
      
      // Require at least 2 characters before searching
      if (this.searchQuery.trim().length < 2) {
        this.results = []
        return
      }
      
      this.debounceTimer = setTimeout(() => {
        this.performSearch()
      }, 300)
    },
    async performSearch() {
      this.isLoading = true
      this.selectedIndex = 0
      
      try {
        const detectedType = this.detectSearchType()
        const query = this.getSearchQuery()
        
        if (detectedType) {
          // User typed a prefix - search that specific type
          this.results = await this.searchByType(detectedType.type, query)
        } else if (this.selectedTopic) {
          // User selected a topic - search within that topic
          this.results = await this.searchByType(this.selectedTopic.type, query)
        } else {
          // No prefix and no topic selected - global search
          this.results = await this.searchGlobal(query)
        }
      } catch (error) {
        alert('Search error:', error)
        // Optionally show error toast if you have toast notifications
        // this.$toast.error(this.$t('search_error'))
      } finally {
        this.isLoading = false
      }
    },
    detectSearchType() {
      const upperQuery = this.searchQuery.toUpperCase()
      return this.enabledSearchTypes.find(type => upperQuery.startsWith(type.prefix))
    },
    getSearchQuery() {
      // Don't remove prefix - send the full query
      return this.searchQuery.trim()
    },
    async searchByType(type, query) {
      const endpoints = {
        client: `/clients?keyword=${encodeURIComponent(query)}`,
        supplier: `/suppliers?keyword=${encodeURIComponent(query)}`,
        product: `/products?keyword=${encodeURIComponent(query)}`,
        inventory: `/inventories?keyword=${encodeURIComponent(query)}`,
        category: `/categories?keyword=${encodeURIComponent(query)}`,
        warehouse: `/warehouses?keyword=${encodeURIComponent(query)}`,
        quote: `/quotes?keyword=${encodeURIComponent(query)}`,
        order: `/orders?keyword=${encodeURIComponent(query)}`,
        delivery: `/deliveries?keyword=${encodeURIComponent(query)}`,
        invoice: `/invoices?keyword=${encodeURIComponent(query)}`,
        return: `/returns?keyword=${encodeURIComponent(query)}`,
        purchase: `/purchases?keyword=${encodeURIComponent(query)}`,
        reception: `/receptions?keyword=${encodeURIComponent(query)}`,
        pos: `/pos?keyword=${encodeURIComponent(query)}`,
        cash_register: `/cash-registers?keyword=${encodeURIComponent(query)}`,
        payment: `/transactions?keyword=${encodeURIComponent(query)}`,
        attachment: `/attachments?keyword=${encodeURIComponent(query)}`,
        project: `/projects?keyword=${encodeURIComponent(query)}`,
      }
      
      if (!endpoints[type]) return []
      
      const { data } = await helper.customGet(`${process.env.VUE_APP_API}${endpoints[type]}`)
      
      // Handle different response formats
      let results = data.data || data.projects || data.clients || data.suppliers || 
                    data.products || data.invoices || data.orders || data
      
      // If results is an object with a data property, use that
      if (results && typeof results === 'object' && !Array.isArray(results) && results.data) {
        results = results.data
      }
      
      // Ensure we have an array
      if (!Array.isArray(results)) {
        results = []
      }
      
      return this.formatResults(results, type)
    },
    async searchGlobal(query) {
      // Get only enabled search types
      const enabledTypes = this.enabledSearchTypes.map(t => t.type);
      
      // Define all possible search types in priority order
      const allSearchTypes = [
        'client',
        'supplier', 
        'product',
        'invoice',
        'order',
        'delivery',
        'reception',
        'purchase',
        'attachment',
        'project',
        'quote',
        'return',
        'warehouse',
        'category',
      ];
      
      // Filter to only enabled types
      const typesToSearch = allSearchTypes.filter(type => enabledTypes.includes(type));
      
      // Create search promises only for enabled types
      const searchPromises = typesToSearch.map(type => this.searchByType(type, query));
      
      let results = await Promise.all(searchPromises);
      results = results.flat();

      return results.slice(0, 20); // Limit to 20 results
    },
    formatResults(data, type) {
      return data.map(item => ({
        id: item.id,
        type: type,
        title: this.getTitle(item, type),
        subtitle: this.getSubtitle(item, type),
        data: item
      }))
    },
    getTitle(item, type) {
      const titleMap = {
        client: item.name,
        supplier: item.name,
        product: item.label || item.name,
        inventory: item.reference,
        category: item.name,
        warehouse: item.name,
        quote: item.reference || `#${item.id}`,
        order: item.reference || `#${item.id}`,
        delivery: item.reference || `#${item.id}`,
        invoice: item.reference || `#${item.id}`,
        return: item.reference || `#${item.id}`,
        purchase: item.reference || `#${item.id}`,
        reception: item.reference || `#${item.id}`,
        pos: item.reference || `#${item.id}`,
        cash_register: item.name,
        payment: item.reference || `#${item.id}`,
        attachment: item.filename || item.name,
        project: item.name || item.title,
      }
      return titleMap[type] || item.name || `#${item.id}`
    },
    getSubtitle(item, type) {
      const subtitleMap = {
        client: item.email || item.phone,
        supplier: item.email || item.phone,
        product: item.reference || item.category?.name,
        inventory: item.product?.label,
        category: `${item.products_count || 0} ${this.$t('products')}`,
        warehouse: item.address,
        quote: item.client?.name,
        order: item.client?.name,
        delivery: item.client?.name,
        invoice: item.client?.name,
        return: item.client?.name,
        purchase: item.supplier?.name,
        reception: item.supplier?.name,
        pos: item.status,
        cash_register: item.warehouse?.name,
        payment: item.amount ? `${item.amount} ${item.currency || ''}` : '',
        attachment: item.filesize ? `${(item.filesize / 1024).toFixed(2)} KB` : (item.created_at || ''),
        project: item.client?.name || item.status,
      }
      return subtitleMap[type] || ''
    },
    getIcon(type) {
      const typeInfo = this.enabledSearchTypes.find(t => t.type === type)
      return typeInfo?.icon || 'bi-search'
    },
    getTypeLabel(type) {
      const typeInfo = this.enabledSearchTypes.find(t => t.type === type)
      return typeInfo?.label || type
    },
    navigateDown() {
      if (this.selectedIndex < this.results.length - 1) {
        this.selectedIndex++
        this.scrollToSelected()
      }
    },
    navigateUp() {
      if (this.selectedIndex > 0) {
        this.selectedIndex--
        this.scrollToSelected()
      }
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const selected = document.querySelector('.search-result-item.active')
        if (selected) {
          selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    selectResult() {
      if (this.results[this.selectedIndex]) {
        this.navigateToResult(this.results[this.selectedIndex])
      }
    },
    navigateToResult(result) {
      const routes = {
        client: `/clients/${result.id}`,
        supplier: `/suppliers/${result.id}`,
        product: `/products/${result.id}`,
        inventory: `/inventories/${result.id}`,
        category: `/categories/${result.id}`,
        warehouse: `/warehouses/${result.id}`,
        quote: `/quotes/${result.id}`,
        order: `/orders/${result.id}`,
        delivery: `/deliveries/${result.id}`,
        invoice: `/invoices/${result.id}`,
        return: `/returns/${result.id}`,
        purchase: `/purchases/${result.id}`,
        reception: `/receptions/${result.id}`,
        pos: `/pos-transactions/${result.id}`,
        cash_register: `/cash-registers/${result.id}`,
        payment: `/transactions/${result.id}`,
        attachment: `/attachments`,
        project: `/projects/${result.id}`,
      }
      
      if (routes[result.type]) {
        this.$router.push(routes[result.type])
        this.closeModal()
      }
    }
  }
}
</script>

<style scoped>
/* Wrapper should not interfere with layout */
.universal-search-wrapper {
  display: inline-block;
  position: relative;
}

/* Search Trigger */
.search-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ffffff36;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 250px;
  position: relative;
  z-index: 1;
}

.search-trigger:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.search-trigger i {
  color: #6c757d;
  font-size: 16px;
}

.search-input-trigger {
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
}

.search-kbd {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  color: #6c757d;
  font-family: monospace;
}

/* Modal Styles */
.custom-modal-backdrop {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99999 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  margin: 0 !important;
}

.custom-modal-dialog {
  width: 100%;
  max-width: 800px;
  margin: auto;
  position: relative;
  z-index: 100000 !important;
}

.search-modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  background: white;
  margin: 1.75rem auto;
  max-height: calc(100vh - 3.5rem);
  display: flex;
  flex-direction: column;
}

/* Modal animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .custom-modal-dialog,
.modal-fade-leave-active .custom-modal-dialog {
  transition: transform 0.3s ease;
}

.modal-fade-enter-from .custom-modal-dialog,
.modal-fade-leave-to .custom-modal-dialog {
  transform: scale(0.9);
}

.search-modal-header {
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.search-icon {
  color: #6c757d;
  font-size: 20px;
}

.search-input-main {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 16px;
  color: #212529;
}

.btn-close-search {
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.btn-close-search:hover {
  color: #212529;
}

/* Topic Selection */
.search-topics {
  margin-top: 16px;
}

.topics-header {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
}

.topics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.topic-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  background: #f8f9fa;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topic-item:hover {
  background: #e9ecef;
  border-color: #0d6efd;
  transform: translateY(-2px);
}

.topic-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 18px;
  margin-bottom: 6px;
}

.topic-label {
  font-size: 11px;
  font-weight: 500;
  color: #495057;
  text-align: center;
}

/* Selected Topic Bar */
.selected-topic-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 10px 12px;
  background: #e7f3ff;
  border: 1px solid #b6d4fe;
  border-radius: 6px;
}

.selected-topic-info {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #084298;
}

.btn-clear-topic {
  background: transparent;
  border: none;
  color: #084298;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-clear-topic:hover {
  background: rgba(13, 110, 253, 0.1);
}

/* Modal Body */
.search-modal-body {
  padding: 0;
  max-height: 450px;
  overflow-y: auto;
  flex: 1;
  min-height: 200px;
}

.search-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
}

.search-results {
  padding: 8px 0;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-result-item:hover,
.search-result-item.active {
  background-color: #f8f9fa;
}

.result-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 18px;
}

.icon-client { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }
.icon-supplier { background: rgba(111, 66, 193, 0.1); color: #6f42c1; }
.icon-product { background: rgba(255, 193, 7, 0.1); color: #ffc107; }
.icon-inventory { background: rgba(13, 202, 240, 0.1); color: #0dcaf0; }
.icon-category { background: rgba(32, 201, 151, 0.1); color: #20c997; }
.icon-warehouse { background: rgba(108, 117, 125, 0.1); color: #6c757d; }
.icon-quote { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }
.icon-order { background: rgba(25, 135, 84, 0.1); color: #198754; }
.icon-delivery { background: rgba(253, 126, 20, 0.1); color: #fd7e14; }
.icon-invoice { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
.icon-return { background: rgba(214, 51, 132, 0.1); color: #d63384; }
.icon-purchase { background: rgba(111, 66, 193, 0.1); color: #6f42c1; }
.icon-reception { background: rgba(13, 202, 240, 0.1); color: #0dcaf0; }
.icon-pos { background: rgba(32, 201, 151, 0.1); color: #20c997; }
.icon-cash_register { background: rgba(25, 135, 84, 0.1); color: #198754; }
.icon-payment { background: rgba(255, 193, 7, 0.1); color: #ffc107; }
.icon-attachment { background: rgba(108, 117, 125, 0.1); color: #6c757d; }
.icon-project { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }

.result-content {
  flex: 1;
  min-width: 0;
}

.result-title {
  font-weight: 600;
  font-size: 14px;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-subtitle {
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-type {
  flex-shrink: 0;
}

.type-badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.badge-client { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }
.badge-supplier { background: rgba(111, 66, 193, 0.1); color: #6f42c1; }
.badge-product { background: rgba(255, 193, 7, 0.1); color: #ffc107; }
.badge-inventory { background: rgba(13, 202, 240, 0.1); color: #0dcaf0; }
.badge-category { background: rgba(32, 201, 151, 0.1); color: #20c997; }
.badge-warehouse { background: rgba(108, 117, 125, 0.1); color: #6c757d; }
.badge-quote { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }
.badge-order { background: rgba(25, 135, 84, 0.1); color: #198754; }
.badge-delivery { background: rgba(253, 126, 20, 0.1); color: #fd7e14; }
.badge-invoice { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
.badge-return { background: rgba(214, 51, 132, 0.1); color: #d63384; }
.badge-purchase { background: rgba(111, 66, 193, 0.1); color: #6f42c1; }
.badge-reception { background: rgba(13, 202, 240, 0.1); color: #0dcaf0; }
.badge-pos { background: rgba(32, 201, 151, 0.1); color: #20c997; }
.badge-cash_register { background: rgba(25, 135, 84, 0.1); color: #198754; }
.badge-payment { background: rgba(255, 193, 7, 0.1); color: #ffc107; }
.badge-attachment { background: rgba(108, 117, 125, 0.1); color: #6c757d; }
.badge-project { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
}

.search-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

/* Initial State */
.search-initial {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
}

.initial-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 50%;
  margin-bottom: 16px;
}

.initial-icon i {
  font-size: 36px;
  color: #adb5bd;
}

.initial-text {
  font-size: 14px;
  margin: 0;
}

/* RTL Support */
[dir="rtl"] .search-trigger {
  flex-direction: row-reverse;
}

[dir="rtl"] .search-input-trigger {
  text-align: right;
}

[dir="rtl"] .search-input-wrapper {
  flex-direction: row-reverse;
}

[dir="rtl"] .search-input-main {
  text-align: right;
}

[dir="rtl"] .topics-header {
  text-align: right;
}

[dir="rtl"] .selected-topic-bar {
  flex-direction: row-reverse;
}

[dir="rtl"] .selected-topic-info {
  flex-direction: row-reverse;
}

[dir="rtl"] .selected-topic-info i {
  margin-right: 0;
  margin-left: 0.5rem;
}

[dir="rtl"] .search-result-item {
  flex-direction: row-reverse;
}

[dir="rtl"] .result-content {
  text-align: right;
}

[dir="rtl"] .result-title,
[dir="rtl"] .result-subtitle {
  text-align: right;
}

[dir="rtl"] .result-type {
  margin-right: auto;
  margin-left: 0;
}
</style>

